import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  sectionPadding,
  HERO_SECTION_PADDING,
  CONTENT_MAX_WIDE,
  CONTENT_MAX_HERO,
  CONTENT_INSET,
} from '../styles/layout';
import { BOOKING_PACKAGES } from '../constants/booking';
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';

const GOLD = '#C9A84C';
const BG = '#090909';
const CARD_BG = '#111111';
const TEXT_MUTED = '#F5F0E8';
const GOLD_BORDER = '1px solid rgba(201, 168, 76, 0.2)';

const FEATURED_CITIES = [
  'Phoenix',
  'Scottsdale',
  'Tempe',
  'Mesa',
  'Glendale',
  'Peoria',
  'Surprise',
  'Goodyear',
  'Gilbert',
  'Chandler',
  'Fountain Hills',
  'Paradise Valley',
  'Litchfield Park',
  'Tolleson',
  'Apache Junction',
  'Queen Creek',
  'Sun Lakes',
  'San Tan Valley',
];

const HERO_BACKGROUND_IMAGE = '/HomepageAI_hero.webp';

const HERO_CONTENT = {
  headingLine1: "Arizona's Premium",
  headingLine2: 'Mobile Golf Simulator',
  subtext:
    'Fully mobile golf bays, set up anywhere the event is happening. From championship courses to custom minigames and tournaments, we bring the fun to any crowd.',
};

const HOW_IT_WORKS_STEPS = [
  {
    number: 1,
    title: 'Book Online',
    description:
      "Pick your date, time, and duration in just a few minutes through our seamless booking flow, and select how long you want the experience to run. You'll get instant confirmation with all the details for your event. No back-and-forth, no phone calls, just a few clicks and you're locked in. A 25% deposit secures your date, and the rest is handled closer to your event.\n\nFor larger events, reach out to events@digitaldivotgolf.com and we'll put together something custom for you.",
  },
  {
    number: 2,
    title: 'We Handle Everything',
    description:
      "Our team arrives at your venue early to handle the full setup from start to finish. We bring the simulator, screen, turf, and all equipment; all you need to provide is the space. Setup takes as little as 30 minutes so your guests walk into a ready-to-play experience. We stay on-site throughout the event to manage the technology, run tournaments and minigames, and keep everything running smoothly. You focus on your guests, we'll handle the rest.",
  },
  {
    number: 3,
    title: 'Play & Enjoy',
    description:
      "Once we're set up, your guests step up to the tee and the experience takes over from there. Choose from over 200 world-class courses and go head-to-head with friends, colleagues, or family in real time. Every shot is tracked with professional-grade accuracy, so the competition stays fierce from the first swing to the last. Whether it's a laid-back backyard gathering or a high-end corporate event, the energy is always electric. All you have to do is show up and enjoy the moment.",
  },
];

const GALLERY_ITEMS = [
  {
    type: 'image',
    src: '/HomepageAI.webp',
    alt: 'Digital Divot mobile golf simulator experience',
  },
  {
    type: 'image',
    src: '/seeitinaction.webp',
    alt: 'Digital Divot simulator in action at an event',
  },
  {
    type: 'video',
    src: '/Seeitinaction4.mp4',
    alt: 'Digital Divot simulator in action video',
  },
];

const ROTATING_EVENT_PHRASES = [
  'Corporate Events',
  'Bachelor Party',
  'Birthday Party',
  'Holiday Party',
  'Company Picnic',
  'Baby Shower',
  'Family Reunion',
];

const shuffleRotatingPhrases = (phrases) => {
  const shuffled = [...phrases];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const ROTATING_PHRASE_MS = 2500;
const ROTATING_PHRASE_TRANSITION_MS = 400;

const Home = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [rotatingPhrases] = useState(() => shuffleRotatingPhrases(ROTATING_EVENT_PHRASES));
  const [rotatingPhraseIndex, setRotatingPhraseIndex] = useState(0);
  const [rotatingPhrasePhase, setRotatingPhrasePhase] = useState('in');
  const [staticHeadlineWidthPx, setStaticHeadlineWidthPx] = useState(null);
  const [rotatingSlotWidthPx, setRotatingSlotWidthPx] = useState(null);
  const [openHowItWorksStep, setOpenHowItWorksStep] = useState(null);
  const [navbarHeightPx, setNavbarHeightPx] = useState(0);
  const staticHeadlineRef = useRef(null);
  const phraseMeasureRef = useRef(null);
  const galleryVideoRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const nav = document.getElementById('dd-main-navbar');
    if (!nav) return undefined;

    const syncNavbarHeight = () => {
      const h = nav.offsetHeight;
      setNavbarHeightPx(h);
      document.documentElement.style.setProperty('--dd-navbar-h', `${h}px`);
    };

    syncNavbarHeight();
    const observer = new ResizeObserver(syncNavbarHeight);
    observer.observe(nav);
    window.addEventListener('resize', syncNavbarHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncNavbarHeight);
    };
  }, [isMobile]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRotatingPhrasePhase('out');
    }, ROTATING_PHRASE_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (rotatingPhrasePhase !== 'out') return undefined;

    const timeout = setTimeout(() => {
      setRotatingPhraseIndex((prev) => (prev + 1) % rotatingPhrases.length);
      setRotatingPhrasePhase('in');
    }, ROTATING_PHRASE_TRANSITION_MS);

    return () => clearTimeout(timeout);
  }, [rotatingPhrasePhase, rotatingPhrases.length]);

  useEffect(() => {
    const el = staticHeadlineRef.current;
    if (!el) return undefined;

    const measure = () => {
      setStaticHeadlineWidthPx(Math.ceil(el.scrollWidth));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile]);

  const rotatingHeadlineFontSize = isMobile
    ? 'clamp(1.1rem, 4.5vw, 1.5rem)'
    : 'clamp(2rem, 4vw, 3rem)';

  useEffect(() => {
    const container = phraseMeasureRef.current;
    if (!container) return undefined;

    const measure = () => {
      const widths = Array.from(container.children).map((el) => el.scrollWidth);
      setRotatingSlotWidthPx(Math.ceil(Math.max(...widths, 0)));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isMobile, rotatingHeadlineFontSize, rotatingPhrases]);

  const galleryItem = GALLERY_ITEMS[galleryIndex];

  // Auto-play the gallery video while it's on screen; pause when scrolled away
  useEffect(() => {
    if (galleryItem.type !== 'video') return undefined;
    const video = galleryVideoRef.current;
    if (!video) return undefined;

    const tryPlay = () => {
      video.play().catch(() => {});
    };

    tryPlay();
    video.addEventListener('loadeddata', tryPlay);
    video.addEventListener('canplay', tryPlay);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          tryPlay();
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(video);
    return () => {
      video.removeEventListener('loadeddata', tryPlay);
      video.removeEventListener('canplay', tryPlay);
      observer.disconnect();
    };
  }, [galleryItem]);

  const goGalleryPrev = () => {
    setGalleryIndex((prev) => (prev - 1 + GALLERY_ITEMS.length) % GALLERY_ITEMS.length);
  };

  const goGalleryNext = () => {
    setGalleryIndex((prev) => (prev + 1) % GALLERY_ITEMS.length);
  };

  const toggleHowItWorksStep = (stepNumber) => {
    setOpenHowItWorksStep((prev) => (prev === stepNumber ? null : stepNumber));
  };

  const pricingCardStyle = {
    backgroundColor: CARD_BG,
    borderRadius: '16px',
    padding: isMobile ? '24px' : '28px',
    border: GOLD_BORDER,
    boxSizing: 'border-box',
  };

  const heroHeight =
    navbarHeightPx > 0
      ? `calc(${isMobile ? '100svh' : '100dvh'} - ${navbarHeightPx}px)`
      : isMobile
        ? '100svh'
        : '100dvh';

  return (
    <div className="dd-page dd-home" style={{ overflowX: 'hidden', backgroundColor: BG }}>
      <Helmet>
        <title>Digital Divot Golf — Premium Mobile Simulator | Phoenix &amp; Scottsdale</title>
        <meta
          name="description"
          content="Arizona's first premium mobile golf simulator rental. Trackman technology, 200+ courses, delivered to your venue in Phoenix and Scottsdale."
        />
        <meta property="og:title" content="Digital Divot Golf — Premium Mobile Simulator" />
        <meta
          property="og:description"
          content="Book a premium mobile golf simulator for your next event in the Phoenix & Scottsdale metro area."
        />
        <meta property="og:image" content="https://digitaldivotgolf.com/HomepageAI_hero.webp" />
        <meta property="og:url" content="https://digitaldivotgolf.com/" />
      </Helmet>
      {/* Hero — full-bleed background image, no split layout */}
      <section
        className="dd-hero-section"
        style={{
          position: 'relative',
          display: 'grid',
          width: '100%',
          height: heroHeight,
          minHeight: heroHeight,
          maxHeight: heroHeight,
          margin: 0,
          marginTop: navbarHeightPx > 0 ? navbarHeightPx : 0,
          padding: 0,
          overflow: 'hidden',
          boxSizing: 'border-box',
          backgroundImage: `url(${HERO_BACKGROUND_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: isMobile ? 'center 35%' : 'center -1in',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div
          aria-hidden
          className="dd-hero-overlay"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />

        <div
          className="dd-hero-content"
          style={{
            position: 'relative',
            gridArea: '1 / 1',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: isMobile ? 'center' : 'flex-start',
            alignItems: isMobile ? 'stretch' : 'center',
            alignSelf: isMobile ? 'stretch' : 'center',
            justifySelf: isMobile ? undefined : 'center',
            height: isMobile ? '100%' : 'auto',
            minHeight: isMobile ? '100%' : undefined,
            margin: isMobile ? undefined : '0 auto',
            padding: isMobile
              ? '1rem max(1rem, env(safe-area-inset-right)) 5.5rem max(1rem, env(safe-area-inset-left))'
              : HERO_SECTION_PADDING,
            maxWidth: isMobile ? CONTENT_MAX_HERO : 'min(48rem, 100%)',
            width: '100%',
            textAlign: isMobile ? 'left' : 'center',
            boxSizing: 'border-box',
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? 'clamp(1.55rem, 5.5vw, 2.5rem)' : 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: '800',
              color: '#F5F0E8',
              lineHeight: 1.1,
              margin: isMobile ? '0 0 8px' : '0 0 20px',
              textShadow: '0 2px 16px rgba(0, 0, 0, 0.65)',
            }}
          >
            <span style={{ display: 'block', whiteSpace: isMobile ? 'normal' : 'nowrap' }}>
              {HERO_CONTENT.headingLine1}
            </span>
            <span style={{ display: 'block', whiteSpace: isMobile ? 'normal' : 'nowrap' }}>
              {HERO_CONTENT.headingLine2}
            </span>
          </h1>
          <div
            className="dd-hero-subtext-pill"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              borderRadius: isMobile ? '12px' : '16px',
              border: '1px solid rgba(201, 168, 76, 0.2)',
              padding: isMobile ? '0.85rem 1.1rem' : '1rem 1.5rem',
              maxWidth: '42rem',
              width: '100%',
              margin: isMobile ? '0 0 12px' : '0 auto 32px',
              boxSizing: 'border-box',
            }}
          >
            <p
              style={{
                fontSize: isMobile ? '0.9375rem' : '1.125rem',
                color: TEXT_MUTED,
                lineHeight: 1.55,
                margin: 0,
              }}
            >
              {HERO_CONTENT.subtext}
            </p>
          </div>
          <div className="dd-hero-cta"
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: isMobile ? 'stretch' : 'center',
              gap: isMobile ? '8px' : '12px',
              marginBottom: isMobile ? '8px' : '28px',
            }}
          >
            <Link
              to="/booking"
              className="dd-hero-cta-btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: isMobile ? '100%' : 'auto',
                alignSelf: isMobile ? 'stretch' : 'center',
                flex: isMobile ? '1 1 auto' : '0 0 auto',
                backgroundColor: GOLD,
                color: '#000000',
                padding: isMobile ? '11px 24px' : '14px 32px',
                borderRadius: '9999px',
                fontWeight: '700',
                fontSize: isMobile ? '0.9375rem' : '1rem',
                textDecoration: 'none',
              }}
            >
              Book Now
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/packages"
              className="dd-hero-cta-btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isMobile ? '100%' : 'auto',
                alignSelf: isMobile ? 'stretch' : 'center',
                flex: isMobile ? '1 1 auto' : '0 0 auto',
                padding: isMobile ? '11px 24px' : '14px 32px',
                borderRadius: '9999px',
                fontWeight: '600',
                fontSize: isMobile ? '0.9375rem' : '1rem',
                textDecoration: 'none',
                color: '#F5F0E8',
                border: `1px solid rgba(201, 168, 76, 0.5)`,
                backgroundColor: 'rgba(0, 0, 0, 0.35)',
                backdropFilter: 'blur(4px)',
              }}
            >
              Learn More
            </Link>
          </div>

        </div>

        <div className="dd-hero-rotating-caption">
          <span
            ref={phraseMeasureRef}
            aria-hidden
            className="dd-rotating-phrase-measure"
            style={{
              position: 'absolute',
              visibility: 'hidden',
              pointerEvents: 'none',
              height: 0,
              overflow: 'hidden',
              fontSize: rotatingHeadlineFontSize,
              fontWeight: '800',
              lineHeight: 1.1,
            }}
          >
            {rotatingPhrases.map((phrase) => (
              <span key={phrase} style={{ display: 'block', whiteSpace: 'nowrap' }}>
                {phrase}
              </span>
            ))}
          </span>
          <h2
            className="dd-rotating-headline"
            style={{
              margin: 0,
              fontSize: rotatingHeadlineFontSize,
              fontWeight: '800',
              lineHeight: 1.1,
            }}
          >
            <span
              ref={staticHeadlineRef}
              className="dd-rotating-headline-static"
              style={
                !isMobile && staticHeadlineWidthPx != null
                  ? {
                      width: staticHeadlineWidthPx,
                      flex: `0 0 ${staticHeadlineWidthPx}px`,
                    }
                  : undefined
              }
            >
              Teeing Off At Your
            </span>
            <span
              className="dd-rotating-headline-slot"
              aria-live="polite"
              style={
                !isMobile && rotatingSlotWidthPx != null
                  ? {
                      width: rotatingSlotWidthPx,
                      flex: `0 0 ${rotatingSlotWidthPx}px`,
                      minWidth: rotatingSlotWidthPx,
                    }
                  : undefined
              }
            >
              <span
                className={
                  rotatingPhrasePhase === 'in'
                    ? 'dd-rotating-phrase-in'
                    : 'dd-rotating-phrase-out'
                }
              >
                {rotatingPhrases[rotatingPhraseIndex]}
              </span>
            </span>
          </h2>
        </div>
      </section>

      {/* How It Works */}
      <section
        style={{
          padding: isMobile
            ? '1.25rem 1rem 1.25rem'
            : 'clamp(1.25rem, 2vw, 1.75rem) clamp(1rem, 4vw, 2rem) clamp(1.25rem, 2.5vw, 1.75rem)',
          backgroundColor: BG,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            maxWidth: CONTENT_MAX_WIDE,
            margin: '0 auto',
            padding: `0 ${CONTENT_INSET}`,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '2rem' : '3rem' }}>
            <h2
              style={{
                fontSize: isMobile ? '1.75rem' : '2.25rem',
                fontWeight: '800',
                color: GOLD,
                margin: 0,
              }}
            >
              How It Works
            </h2>
          </div>

          <div className="dd-how-it-works-steps" style={{ width: '100%' }}>
            {HOW_IT_WORKS_STEPS.map((step, index) => {
              const isOpen = openHowItWorksStep === step.number;

              return (
                <div key={step.number} className="dd-how-step-row">
                  <span className="dd-how-step-bg-num" aria-hidden="true">
                    {String(step.number).padStart(2, '0')}
                  </span>
                  <div className="dd-how-step-content">
                    <button
                      type="button"
                      className="dd-how-step-trigger"
                      onClick={() => toggleHowItWorksStep(step.number)}
                      aria-expanded={isOpen}
                      aria-controls={`how-it-works-panel-${step.number}`}
                      id={`how-it-works-trigger-${step.number}`}
                    >
                      <h3 className="dd-how-step-title">{step.title}</h3>
                      <ChevronDown
                        size={isMobile ? 22 : 26}
                        className={`dd-how-step-chevron${isOpen ? ' dd-how-step-chevron-open' : ''}`}
                        aria-hidden="true"
                      />
                    </button>
                    <div
                      id={`how-it-works-panel-${step.number}`}
                      role="region"
                      aria-labelledby={`how-it-works-trigger-${step.number}`}
                      className={`dd-how-step-panel${isOpen ? ' dd-how-step-panel-open' : ''}`}
                    >
                      <div className="dd-how-step-panel-inner">
                        {step.description.split('\n\n').map((paragraph, paragraphIndex) => (
                          <p key={paragraphIndex} className="dd-how-step-description">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                  {index < HOW_IT_WORKS_STEPS.length - 1 && (
                    <hr className="dd-how-step-divider" />
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ textAlign: 'center', marginTop: isMobile ? '2.5rem' : '3.5rem' }}>
            <Link
              to="/booking"
              className="dd-hero-cta-btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: 'auto',
                flex: '0 0 auto',
                backgroundColor: GOLD,
                color: '#000000',
                padding: isMobile ? '12px 28px' : '14px 32px',
                borderRadius: '9999px',
                fontWeight: '700',
                fontSize: '1rem',
                textDecoration: 'none',
              }}
            >
              Book Your Event
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section
        style={{
          padding: sectionPadding(isMobile),
          backgroundColor: BG,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            maxWidth: CONTENT_MAX_WIDE,
            margin: '0 auto',
            padding: `0 ${CONTENT_INSET}`,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '28px' : '36px' }}>
            <h2
              style={{
                fontSize: isMobile ? '1.75rem' : '2.25rem',
                fontWeight: '800',
                color: GOLD,
                marginBottom: '12px',
              }}
            >
              See It In Action
            </h2>
            <p
              style={{
                fontSize: '1rem',
                color: TEXT_MUTED,
                margin: 0,
                lineHeight: 1.6,
                maxWidth: '36rem',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              Immersive Experience at Events Across The Valley
            </p>
          </div>

          <div
            className="dd-gallery-controls"
            style={{
              position: 'relative',
              maxWidth: '56rem',
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '8px' : '16px',
            }}
          >
            <button
              type="button"
              onClick={goGalleryPrev}
              aria-label="Previous slide"
              style={{
                flexShrink: 0,
                width: isMobile ? '40px' : '48px',
                height: isMobile ? '40px' : '48px',
                borderRadius: '50%',
                border: GOLD_BORDER,
                backgroundColor: CARD_BG,
                color: GOLD,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <ChevronLeft size={isMobile ? 22 : 28} />
            </button>

            <div
              style={{
                flex: 1,
                minWidth: 0,
                borderRadius: '16px',
                overflow: 'hidden',
                border: GOLD_BORDER,
                backgroundColor: CARD_BG,
                boxSizing: 'border-box',
              }}
            >
              {galleryItem.type === 'video' ? (
                <video
                  key={galleryItem.src}
                  ref={galleryVideoRef}
                  src={galleryItem.src}
                  muted
                  loop
                  playsInline
                  autoPlay
                  disablePictureInPicture
                  disableRemotePlayback
                  preload="auto"
                  aria-label={galleryItem.alt}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    aspectRatio: '16 / 9',
                    objectFit: 'cover',
                    backgroundColor: '#000',
                    pointerEvents: 'none',
                  }}
                />
              ) : (
                <img
                  key={galleryItem.src}
                  src={galleryItem.src}
                  alt={galleryItem.alt}
                  loading="lazy"
                  width={1600}
                  height={900}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    aspectRatio: '16 / 9',
                    objectFit: 'cover',
                  }}
                />
              )}
            </div>

            <button
              type="button"
              onClick={goGalleryNext}
              aria-label="Next slide"
              style={{
                flexShrink: 0,
                width: isMobile ? '40px' : '48px',
                height: isMobile ? '40px' : '48px',
                borderRadius: '50%',
                border: GOLD_BORDER,
                backgroundColor: CARD_BG,
                color: GOLD,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <ChevronRight size={isMobile ? 22 : 28} />
            </button>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              marginTop: '16px',
            }}
          >
            {GALLERY_ITEMS.map((item, index) => (
              <button
                key={item.src}
                type="button"
                className="dd-dot-btn"
                aria-label={`Go to slide ${index + 1}`}
                onClick={() => setGalleryIndex(index)}
                style={{
                  height: '8px',
                  width: galleryIndex === index ? '24px' : '8px',
                  borderRadius: '999px',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  backgroundColor:
                    galleryIndex === index ? GOLD : 'rgba(156, 163, 175, 0.5)',
                  transition: 'width 0.25s ease, background-color 0.25s ease',
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section
        style={{
          padding: isMobile
            ? '1rem 1rem 2rem'
            : 'clamp(1rem, 3vw, 2.5rem) clamp(1rem, 4vw, 2rem) clamp(2rem, 6vw, 5rem)',
          backgroundColor: BG,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            maxWidth: CONTENT_MAX_WIDE,
            margin: '0 auto',
            padding: `0 ${CONTENT_INSET}`,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '28px' : '36px' }}>
            <h2
              style={{
                fontSize: isMobile ? '1.75rem' : '2.25rem',
                fontWeight: '800',
                color: GOLD,
                marginBottom: '12px',
              }}
            >
              Choose Your Experience
            </h2>
            <p
              style={{
                color: TEXT_MUTED,
                fontSize: '1.0625rem',
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              One premium mobile golf setup. Three ways to play.
            </p>
          </div>

          <div className="dd-home-pricing-grid">
            {BOOKING_PACKAGES.map((pkg) => (
              <div
                key={pkg.id}
                style={{
                  ...pricingCardStyle,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <p
                  style={{
                    color: GOLD,
                    fontWeight: '700',
                    fontSize: '0.8125rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: '6px',
                    marginTop: 0,
                  }}
                >
                  {pkg.name}
                </p>
                <p
                  style={{
                    fontSize: isMobile ? '1.05rem' : '1.125rem',
                    fontWeight: '700',
                    color: '#F5F0E8',
                    margin: '0 0 10px',
                    lineHeight: 1.3,
                  }}
                >
                  {pkg.hook}
                </p>
                <p
                  style={{
                    fontSize: isMobile ? '1.75rem' : '2rem',
                    fontWeight: '800',
                    color: '#F5F0E8',
                    marginBottom: '4px',
                  }}
                >
                  ${pkg.basePrice.toLocaleString()}
                  <span style={{ fontSize: '1rem', fontWeight: '500', color: '#F5F0E8' }}>
                    {' '}
                    {pkg.priceDescriptor}
                  </span>
                </p>
                <p
                  style={{
                    color: TEXT_MUTED,
                    fontSize: '0.9375rem',
                    lineHeight: 1.65,
                    margin: '12px 0 0',
                    textAlign: 'left',
                  }}
                >
                  {pkg.description}
                </p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: isMobile ? '28px' : '36px' }}>
            <Link to="/packages" className="dd-editorial-link">
              See It For Yourself{' '}
              <span className="dd-editorial-link-arrow" aria-hidden="true">
                →
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Where We Play — compact service strip */}
      <section
        style={{
          padding: isMobile ? '0.875rem 0' : '1rem 0 1.125rem',
          backgroundColor: BG,
        }}
      >
        <p
          style={{
            margin: '0 0 0.625rem',
            padding: `0 ${CONTENT_INSET}`,
            fontSize: isMobile ? '0.6875rem' : '0.75rem',
            fontWeight: '600',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: GOLD,
            textAlign: 'center',
          }}
        >
          Serving the Greater Phoenix Area
        </p>

        <div className="dd-city-marquee">
          <div className="dd-city-marquee-track">
            {[...FEATURED_CITIES, ...FEATURED_CITIES].map((city, index) => (
              <span
                key={`${city}-${index}`}
                className="dd-city-marquee-pill"
                aria-hidden={index >= FEATURED_CITIES.length}
              >
                {city}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
